import AddEditUmUimDialog from "@/components/Forms/AddEditUmUimDialog.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useDeleteParty } from "@/mutations/useDeleteParty.tsx";
import { type MrtParty, useFetchMrtPartiesForCase } from "@/queries/useFetchMrtPartiesForCase.ts";
import { getErrorMessage } from "@/utils/api.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton } from "@mui/material";
import {
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Row,
    type MRT_SortingState,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";

type Props = {
    caseIdUUID: string;
    roleName?: string[];
};

const MrtUmUimTable = ({ caseIdUUID, roleName }: Props) => {
    const { hasPermission } = useEmployee();
    const deleteParty = useDeleteParty();
    const [editPartyId, setEditPartyId] = useState<string | undefined>();
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });

    const { data, isError, isRefetching, isLoading } = useFetchMrtPartiesForCase({
        caseIdUUID,
        roleName,
        columnFilters,
        globalFilter,
        pagination,
        sorting,
    });

    const handleEditRow = (idUUID: string) => {
        setEditPartyId(idUUID);
    };

    const handleDeleteRow = (row: MRT_Row<MrtParty>) => {
        if (window.confirm("Are you sure you want to delete this UM/UIM Information?")) {
            deleteParty.mutate(
                {
                    partyUUID: row.original.idUUID,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("UM/UIM Information removed.", { variant: "success" });
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: UM/UIM Information could not be removed.", {
                            variant: "error",
                        });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        }
    };

    const columns = useMemo<MRT_ColumnDef<MrtParty>[]>(() => {
        const columnDefs: MRT_ColumnDef<MrtParty>[] = [
            {
                accessorKey: "edit",
                header: "",
                enableSorting: false,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditRow(row.original.idUUID);
                        }}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                ),
                size: 50,
            },
            {
                accessorKey: "claimNumber",
                header: "Claim number",
            },
            {
                accessorKey: "companyName",
                header: "Insurance Company",
            },
            {
                accessorKey: "idAdjusterView",
                header: "Adjuster",
            },
            {
                accessorKey: "phoneNumber",
                header: "Adjuster Phone",
            },
            {
                accessorKey: "phoneExtension",
                header: "Extension",
            },
            {
                accessorKey: "coverageType",
                header: "Coverage Type",
            },
            {
                accessorKey: "policyPersonCoverageMM",
                header: "UM/UIM Coverage",
            },
            {
                accessorKey: "policyVehiculeCoverageMM",
                header: "Property Damage Coverage",
            },
            {
                accessorKey: "medpay",
                header: "Medpay",
            },
        ];

        if (hasPermission("delete:um/uim-info")) {
            columnDefs.push({
                accessorKey: "delete",
                header: "Delete",
                size: 50,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleDeleteRow(row);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                ),
            });
        }

        return columnDefs;
    }, [hasPermission, handleEditRow, handleEditRow, handleDeleteRow]);

    const table = useMaterialReactTable({
        columns,
        data: isLoading || isRefetching || isError || data === undefined ? [] : data.data,
        enableColumnActions: false,
        defaultColumn: {
            size: 30,
            muiTableBodyCellProps: {
                sx: {
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                },
            },
        },
        initialState: {
            showColumnFilters: false,
        },
        enableTopToolbar: isError,
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        muiToolbarAlertBannerProps: isError
            ? {
                  color: "error",
                  children: "Error loading data",
              }
            : undefined,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        // renderTopToolbarCustomActions: () => (
        //     <Tooltip arrow title="Refresh Data">
        //         <IconButton onClick={() => refetch()}>
        //             <RefreshIcon />
        //         </IconButton>
        //     </Tooltip>
        // ),
        rowCount: data?.meta?.totalRowCount ?? 0,
        state: {
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            showLoadingOverlay: isLoading || isRefetching,
            sorting,
        },
    });

    return (
        <>
            <MaterialReactTable table={table} />
            <AddEditUmUimDialog
                caseUUID={caseIdUUID}
                showAddButton={false}
                editPartyId={editPartyId}
                clearEditPartyId={() => {
                    setEditPartyId(undefined);
                }}
            />
        </>
    );
};

export default MrtUmUimTable;
